import PropTypes from 'prop-types'

import {toQueryString} from '@s-ui/js/lib/string'
import AtomButton, {atomButtonDesigns} from '@s-ui/react-atom-button'
import AtomIcon, {ATOM_ICON_SIZES} from '@s-ui/react-atom-icon'
import {useSuiContext} from '@s-ui/react-context'
import PrimitiveTypography, {primitiveTypographyDesign} from '@s-ui/react-primitive-typography'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import {NET_INPUT_ID} from '@adv-mt/mt-net-input'
import IconArrowLeft from '@adv-mt/mt-svg-icons/lib/ArrowLeft'
import IconChatConversationOutline from '@adv-mt/mt-svg-icons/lib/ChatConversationOutline'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close'
import IconExpand from '@adv-mt/mt-svg-icons/lib/Expand'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

const BASE_CLASS = 'mt-NetDrawer-header'

const NetDrawerHeader = ({
  title,
  chatId,
  context,
  pageName,
  showBackButton,
  showEditButton,
  showHistoryButton,
  showExpandButton,
  showLogo,
  onBack,
  onHistory,
  onClose
}) => {
  const {domain, i18n} = useSuiContext()
  const {track} = useAnalyticsTrack()

  const config = domain.get('config')
  const {TRACKING_EVENT_NAMES: {NET_FULL_SCREEN_CLICKED} = {}} = config.get('tracking')
  const {DRAWER_REOPEN_SESSION_KEY, NET_INPUT_USER_DEFAULT_MESSAGE_SESSION_KEY, NET_ASSISTANT_PAGE_FULL_WIDTH_IPROMO} =
    config.get('COMMON').AI_ASSISTANT
  const {NET_PATH_PATTERN} = config.get('page')
  const urlStaticsImages = config.get('URL_STATICS_IMAGES')
  const netLogoUrl = config.get('COMMON').AI_ASSISTANT.NET_LOGO_URL_XS
  const logoUrl = `${urlStaticsImages}${netLogoUrl}`

  const isMobile = config.get('isMobile')
  const isHigherThanMobileResolution = useMediaQuery(useMediaQueryBreakpoints.sm, {
    defaultMatches: !isMobile
  })

  const onClickToExpand = () => {
    // Store the input value temporarily so the Net page input can retrieve it
    const textarea = document.getElementById(NET_INPUT_ID)
    const currentInputValue = textarea?.value?.trim() || ''
    if (currentInputValue) {
      sessionStorage.setItem(NET_INPUT_USER_DEFAULT_MESSAGE_SESSION_KEY, currentInputValue)
    }

    // Set the reopen key to be able to reopen drawer when coming back
    sessionStorage.setItem(DRAWER_REOPEN_SESSION_KEY, 'true')

    track({event: NET_FULL_SCREEN_CLICKED, data: {hit_information: chatId}})

    const queryParams = toQueryString({
      ipromo: NET_ASSISTANT_PAGE_FULL_WIDTH_IPROMO,
      chatId,
      adId: context?.page?.adId,
      pageType: context?.page?.type,
      pageName
    })
    window.location.href = `${NET_PATH_PATTERN}/?${queryParams}`
  }

  return (
    <div className={BASE_CLASS}>
      <div className={`${BASE_CLASS}-left`}>
        {showBackButton && (
          <AtomButton
            onClick={onBack}
            aria-label={i18n.t('NET.BACK')}
            design={atomButtonDesigns.FLAT}
            leftIcon={
              <AtomIcon size={ATOM_ICON_SIZES.medium}>
                <IconArrowLeft />
              </AtomIcon>
            }
          />
        )}
        {title && (
          <PrimitiveTypography design={primitiveTypographyDesign.HEADLINE_2} className={`${BASE_CLASS}-title`}>
            {title}
          </PrimitiveTypography>
        )}
        {showLogo && <img src={logoUrl} className={`${BASE_CLASS}-logo`} alt="logo-net-ai-assistant" />}
      </div>

      <div className={`${BASE_CLASS}-right`}>
        {showHistoryButton && (
          <AtomButton
            onClick={onHistory}
            aria-label={i18n.t('NET.CHAT_HISTORY.HEADER_TITLE')}
            design={atomButtonDesigns.FLAT}
            leftIcon={
              <AtomIcon size={ATOM_ICON_SIZES.medium}>
                <IconChatConversationOutline />
              </AtomIcon>
            }
          />
        )}
        {showExpandButton && isHigherThanMobileResolution && (
          <AtomButton
            onClick={onClickToExpand}
            aria-label={i18n.t('NET.EXPAND')}
            design={atomButtonDesigns.FLAT}
            leftIcon={
              <AtomIcon size={ATOM_ICON_SIZES.medium}>
                <IconExpand />
              </AtomIcon>
            }
          />
        )}
        <AtomButton
          onClick={onClose}
          aria-label={i18n.t('NET.CLOSE_CHAT')}
          design={atomButtonDesigns.FLAT}
          leftIcon={
            <AtomIcon size={ATOM_ICON_SIZES.medium}>
              <IconClose />
            </AtomIcon>
          }
        />
      </div>
    </div>
  )
}

NetDrawerHeader.displayName = 'NetDrawerHeader'

NetDrawerHeader.propTypes = {
  title: PropTypes.string,
  chatId: PropTypes.string,
  context: PropTypes.object,
  pageName: PropTypes.object,
  showBackButton: PropTypes.bool,
  showEditButton: PropTypes.bool,
  showHistoryButton: PropTypes.bool,
  showExpandButton: PropTypes.bool,
  showLogo: PropTypes.bool,
  onBack: PropTypes.func,
  onHistory: PropTypes.func,
  onClose: PropTypes.func.isRequired
}

export default NetDrawerHeader
